import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
  COMPOSITION_ENTITY_DATA_V3,
  PositionsEntityKind,
} from '@revelio/data-access';
import { Callout, Link, Spinner, Text } from '@revelio/ui';

import { useCompositionV3QueryWithStatus } from '../CustomDash/components/DataResolver/use-composition-v3-query';
import { buildCompositionV3Variables } from '../CustomDash/filters/utils/composition-v3/build-composition-v3-variables';
import { positionsEntityKindToEntKind } from '../CustomDash/filters/utils/role-skill-ent-kind';
import DashboardPage from '../DashboardPage';
import styles from './entity-search-results.module.css';

const ENTITY_TYPES = Object.values(PositionsEntityKind);

const BackToSearch = () => {
  return (
    <Link color="normal" href="/tools/find">
      Back to Search
    </Link>
  );
};

const EntityCard = ({
  entityType,
  entityId,
}: {
  entityType: PositionsEntityKind;
  entityId: number;
}) => {
  const req = useMemo(
    () =>
      buildCompositionV3Variables({
        entities: [
          {
            EntType: positionsEntityKindToEntKind(entityType),
            EntID: entityId,
          },
        ],
      }),
    [entityType, entityId]
  );

  const [result] = useCompositionV3QueryWithStatus({
    query: COMPOSITION_ENTITY_DATA_V3,
    variables: { req },
    context: useMemo(
      () => ({
        skipGlobalErrorHandling: true,
      }),
      []
    ),
  });

  const entityData = result.data?.composition_v3?.entities?.[0];

  if (result.fetching) {
    return (
      <div className={styles.card}>
        <div className={styles.cardBody}>
          <div className={styles.loadingState}>
            <Spinner size="sm" />
            <Text>Loading entity {entityId}...</Text>
          </div>
        </div>
      </div>
    );
  }

  if (result.error) {
    return (
      <div className={styles.card}>
        <div className={styles.cardBody}>
          <Callout variant="error">
            Error loading entity {entityId}: {result.error.message}
          </Callout>
        </div>
      </div>
    );
  }

  if (!entityData) {
    return (
      <div className={styles.card}>
        <div className={styles.cardBody}>
          <Callout variant="warning">
            No entity found for {entityType} with ID {entityId}
          </Callout>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardBody}>
        <div className={styles.entityDetails}>
          <Text as="h2" fontSize="md" weight="bold">
            {entityData.long_name}
          </Text>
          <Text>
            <strong>ID:</strong> {entityData.id}
          </Text>
          <Text>
            <strong>Short Name:</strong> {entityData.short_name}
          </Text>
          <Text>
            <strong>Long Name:</strong> {entityData.long_name}
          </Text>
          <Text>
            <strong>Type:</strong> {entityType}
          </Text>
        </div>
      </div>
    </div>
  );
};

export const EntitySearchResults = () => {
  const { entityType, id } = useParams<{
    entityType: PositionsEntityKind;
    id: string;
  }>();

  const isValidEntityType = entityType && ENTITY_TYPES.includes(entityType);

  // Parse multiple entity IDs from comma-separated string
  const entityIds = useMemo(() => {
    if (!id) return [];
    return (
      id
        .split(',')
        .map((idStr) => idStr.trim())
        // biome-ignore lint/suspicious/noGlobalIsNan: migrated from develop
        .filter((idStr) => idStr && !isNaN(Number(idStr)))
        // biome-ignore lint/correctness/useParseIntRadix: migrated from develop
        .map((idStr) => parseInt(idStr))
    );
  }, [id]);

  if (!isValidEntityType || entityIds.length === 0) {
    return (
      <DashboardPage title="Entity Search Results" backPath="/tools/find">
        <div className={styles.card}>
          <div className={styles.cardBody}>
            <Callout variant="warning">
              Invalid entity type or no valid entity IDs provided.
            </Callout>
          </div>
        </div>
      </DashboardPage>
    );
  }

  return (
    <DashboardPage title="Entity Details" backPath="/tools/find">
      <div className={styles.container}>
        <div className={styles.header}>
          <Text as="h2" fontSize="lg" weight="bold">
            {`Searching ${entityIds.length} Entities`}
          </Text>
          <BackToSearch />
        </div>

        <div className={styles.scrollArea}>
          <div className={styles.cardGrid}>
            {entityIds.map((entityId) => (
              <EntityCard
                key={entityId}
                entityType={entityType}
                entityId={entityId}
              />
            ))}
          </div>
        </div>
      </div>
    </DashboardPage>
  );
};
